import { useEffect, useMemo, useState } from "react";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Chunky birthday picker: three tap-friendly selects for day / month / year
 * that match the 67Cafe visual language. Works reliably on iOS/Android
 * without needing react-day-picker.
 *
 * The picker holds its OWN partial state so a user can pick Year first (or
 * Month first, etc.) without the selection being wiped. The `onChange`
 * callback only fires with a full ISO YYYY-MM-DD once all three parts are
 * chosen (and with "" while incomplete).
 */
const ChunkyDatePicker = ({
  value = "",
  onChange,
  testIdPrefix = "welcome-dob",
  minYear,
  maxYear,
}) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const yMin = minYear ?? currentYear - 100;
  const yMax = maxYear ?? currentYear;

  // Local partial state — seeded from `value` and kept live between clicks
  // so incomplete choices are not lost when the parent's state is still "".
  const [d, setD] = useState("");
  const [m, setM] = useState("");
  const [y, setY] = useState("");

  // Sync down from the parent when it explicitly sets a full ISO date (or
  // resets to ''). We only overwrite local state when the parent's date
  // differs from what we would emit, so mid-typing doesn't get clobbered.
  useEffect(() => {
    if (!value) {
      if (!d && !m && !y) return; // already clean
      return; // parent cleared, but user is mid-selection — keep local
    }
    const [ny, nm, nd] = value.split("-").map((n) => Number(n) || "");
    if (ny !== y || nm !== m || nd !== d) {
      setY(ny);
      setM(nm);
      setD(nd);
    }
  }, [value, d, m, y]);

  const years = useMemo(() => {
    const arr = [];
    for (let i = yMax; i >= yMin; i--) arr.push(i);
    return arr;
  }, [yMin, yMax]);

  const daysInMonth = useMemo(() => {
    if (!m || !y) return 31;
    return new Date(y, m, 0).getDate();
  }, [m, y]);

  // Central "commit" — updates the local {d,m,y} for whichever field was
  // touched, clamps the day if the resulting month/year has fewer days, and
  // emits an ISO string (or "") to the parent. Extracted so every handler
  // gets the exact same behavior — the Year handler used to skip the clamp
  // and produce invalid dates like 2021-02-31.
  const commit = (partial) => {
    const next = { d, m, y, ...partial };
    if (next.m && next.y) {
      const cap = new Date(next.y, next.m, 0).getDate();
      if (next.d && next.d > cap) next.d = cap;
    }
    setD(next.d);
    setM(next.m);
    setY(next.y);
    if (next.d && next.m && next.y) {
      const iso = `${String(next.y).padStart(4, "0")}-${String(next.m).padStart(2, "0")}-${String(next.d).padStart(2, "0")}`;
      // Round-trip guard against impossible dates.
      const check = new Date(next.y, next.m - 1, next.d);
      if (
        check.getFullYear() === next.y &&
        check.getMonth() === next.m - 1 &&
        check.getDate() === next.d
      ) {
        onChange(iso);
        return;
      }
    }
    onChange("");
  };

  const selectStyle = {
    border: "3px solid #2b2b2b",
    boxShadow: "0 5px 0 #2b2b2b",
    background: "#EAF6FF",
    color: "#484848",
    fontSize: 16,
    borderRadius: 16,
    padding: "10px 12px",
    outline: "none",
    fontFamily: "Fredoka, sans-serif",
    fontWeight: 600,
    textAlign: "center",
    minWidth: 0,
    // Custom chevron so the field reads as a dropdown, not a button.
    appearance: "none",
    WebkitAppearance: "none",
    backgroundImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%232b2b2b' d='M0 0h12L6 8z'/></svg>\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
    paddingRight: 28,
  };

  return (
    <div
      className="grid grid-cols-3 gap-2 w-full"
      data-testid={`${testIdPrefix}-picker`}
    >
      <select
        aria-label="Day"
        data-testid={`${testIdPrefix}-day`}
        value={d || ""}
        onChange={(e) => commit({ d: Number(e.target.value) || "" })}
        style={selectStyle}
      >
        <option value="">Day</option>
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </select>
      <select
        aria-label="Month"
        data-testid={`${testIdPrefix}-month`}
        value={m || ""}
        onChange={(e) => commit({ m: Number(e.target.value) || "" })}
        style={selectStyle}
      >
        <option value="">Month</option>
        {MONTHS.map((label, i) => (
          <option key={label} value={i + 1}>{label}</option>
        ))}
      </select>
      <select
        aria-label="Year"
        data-testid={`${testIdPrefix}-year`}
        value={y || ""}
        onChange={(e) => commit({ y: Number(e.target.value) || "" })}
        style={selectStyle}
      >
        <option value="">Year</option>
        {years.map((yr) => (
          <option key={yr} value={yr}>{yr}</option>
        ))}
      </select>
    </div>
  );
};

export default ChunkyDatePicker;
